// File: /opt/stacks/workout/app/src/hooks/useMediaSession.js
//
// Sets up the Media Session API so Android Chrome shows workout controls
// in the notification shade and lock screen when the browser is backgrounded.
// A silent audio loop keeps the browser's audio session alive so the
// notification persists between Tone.js sound bursts.

import { useEffect } from "react";

// Minimal valid silent MP3 (~140 bytes) encoded as a data URI.
// Loops continuously to maintain an active audio session for Chrome Android.
const SILENT_MP3 =
  "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRBqSAAAAAAAAAAAAAAAAAAAA";

// Module-level silent audio element — created once, started during a user
// gesture so it survives Chrome's autoplay policy.
let sharedAudio = null;

/**
 * Call during a user gesture (e.g. the Start / Play button click) to unlock
 * the silent audio track.  This ensures the Media Session notification
 * persists between Tone.js sound bursts.
 */
export function unlockMediaAudio() {
  if (!sharedAudio) {
    sharedAudio = new Audio(SILENT_MP3);
    sharedAudio.loop = true;
    sharedAudio.volume = 0;
  }
  // play() inside a user gesture will succeed; outside it may be blocked
  sharedAudio.play().catch(() => {});
}

/**
 * @param {object} params
 * @param {boolean} params.isActive     - true during running or paused workout
 * @param {boolean} params.isPlaying    - true when timer is actively running
 * @param {string}  params.exerciseName - shown as notification title
 * @param {string}  params.workoutName  - shown as notification album/subtitle
 * @param {Function} params.onPlay      - called when user taps Play in notification
 * @param {Function} params.onPause     - called when user taps Pause in notification
 */
const useMediaSession = ({
  isActive,
  isPlaying,
  exerciseName,
  workoutName,
  onPlay,
  onPause,
}) => {
  const supported = "mediaSession" in navigator;

  // Keep the silent audio playing while the session is active, pause when not.
  useEffect(() => {
    if (!sharedAudio) return;
    if (isActive) {
      sharedAudio.play().catch(() => {});
    } else {
      sharedAudio.pause();
    }
  }, [isActive]);

  // Stop the loop when the view goes away mid-session (going live swaps the
  // Coach for the live view, and an ended live session unmounts it) so a
  // silent track never keeps playing with nothing to show for it.
  useEffect(
    () => () => {
      sharedAudio?.pause();
      if (supported) {
        navigator.mediaSession.metadata = null;
        navigator.mediaSession.playbackState = "none";
      }
    },
    [supported],
  );

  // Update metadata when exercise or workout name changes
  useEffect(() => {
    if (!supported || !isActive) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: exerciseName || "Workout",
      artist: "Khronos",
      album: workoutName || "Khronos Workout",
    });
  }, [supported, isActive, exerciseName, workoutName]);

  // Register play/pause action handlers
  useEffect(() => {
    if (!supported || !isActive) return;
    navigator.mediaSession.setActionHandler("play", onPlay);
    navigator.mediaSession.setActionHandler("pause", onPause);
    return () => {
      navigator.mediaSession.setActionHandler("play", null);
      navigator.mediaSession.setActionHandler("pause", null);
    };
  }, [supported, isActive, onPlay, onPause]);

  // Sync playback state
  useEffect(() => {
    if (!supported) return;
    navigator.mediaSession.playbackState = isActive
      ? isPlaying
        ? "playing"
        : "paused"
      : "none";
  }, [supported, isActive, isPlaying]);

  // Clear metadata when session ends
  useEffect(() => {
    if (!supported) return;
    if (!isActive) {
      navigator.mediaSession.metadata = null;
    }
  }, [supported, isActive]);
};

export default useMediaSession;
